import { extractNumericEntityIdFromDataId, extractEntityIdFromDataId } from './treeUtils';
import { TreeNodeFactory } from './treeNodeFactory';
import { TreeNode } from '@/types/tree';
import { IBackend } from '@/api/BackendApi';
import { ApiException, SampleActivityDto, UpdateSampleActivityRequest } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { invalidateSampleGridCache } from '@/lib/cache/sampleGridCache';
import { getProblemDetail } from '@/lib/utils/apiErrors';

/**
 * Interface for submit operation dependencies
 */
export interface ISubmitDependencies {
  showSnackbar: (
    message: string,
    severity?: 'success' | 'error' | 'warning' | 'info',
    autoHideDuration?: number | null,
    title?: string
  ) => void;
  showConfirmDialog: (config: {
    title: string;
    message: string;
    confirmText?: string;
    isDestructive?: boolean;
  }) => Promise<boolean>;
  getData?: (dataId: string) => any;
}

/**
 * Dependencies for tree refresh after state-changing actions.
 */
export interface IActionRefreshDependencies {
  reloadNode: (nodeId: string, backend: IBackend) => Promise<void>;
  clearChildrenLoaded: (nodeId: string) => void;
  invalidateChildrenCache: (nodeId: string) => void;
  loadChildren: (nodeId: string, backend: IBackend) => Promise<void>;
  expandNode?: (nodeId: string) => void;
}

/**
 * Controls how tree refresh runs after an action.
 */
export interface IActionRefreshOptions {
  /** Reload submitted sample children so activity status changes are immediately visible. */
  reloadNodeChildrenWhenSample?: boolean;
  /** Reload parent subtree (used by reset where parent sample status/children may change). */
  reloadParentSubtree?: boolean;
}

/**
 * Performs the actual submit operation based on node type
 * This is the core submission logic shared between useSubmitEntity and actionHandlers
 */
export async function performSubmitOperation(
  node: TreeNode,
  backend: IBackend,
  userHasConfirmed: boolean = false
): Promise<any> {
  let updatedData;
  switch (node.type) {
    case 'sample': {
      updatedData = await backend.sample.submit(extractEntityIdFromDataId(node.dataId).toString(), userHasConfirmed);
      invalidateSampleGridCache();
      break;
    }
    case 'sampleActivity': {
      updatedData = await backend.sampleActivity.submit(
        extractNumericEntityIdFromDataId(node.dataId),
        userHasConfirmed
      );
      break;
    }
    case 'projectActivity': {
      updatedData = await backend.projectActivity.submit(
        extractNumericEntityIdFromDataId(node.dataId),
        userHasConfirmed
      );
      break;
    }
    case 'theoryActivity': {
      updatedData = await backend.theoryActivity.submit(
        extractNumericEntityIdFromDataId(node.dataId),
        userHasConfirmed
      );
      break;
    }
    case 'dataPackage': {
      updatedData = await backend.data.submit(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    case 'shipping': {
      updatedData = await backend.shipping.submit(extractNumericEntityIdFromDataId(node.dataId));
      break;
    }
    default:
      throw new Error(`Unsupported node type for submit: ${node.type}`);
  }

  log.debug('[submitEntityUtils] Node submitted successfully:', {
    nodeId: node.nodeId,
    type: node.type,
    userHasConfirmed,
  });

  return updatedData;
}

/**
 * Refreshes tree state after action execution.
 */
export async function refreshAfterAction(
  node: TreeNode,
  backend: IBackend,
  deps: IActionRefreshDependencies,
  options: IActionRefreshOptions = {}
): Promise<void> {
  await deps.reloadNode(node.nodeId, backend);

  if (options.reloadNodeChildrenWhenSample && node.type === 'sample') {
    deps.clearChildrenLoaded(node.nodeId);
    deps.invalidateChildrenCache(node.nodeId);
    await deps.loadChildren(node.nodeId, backend);
    deps.expandNode?.(node.nodeId);
  }

  if (options.reloadParentSubtree && node.parentId) {
    await deps.reloadNode(node.parentId, backend);
    deps.invalidateChildrenCache(node.parentId);
    deps.clearChildrenLoaded(node.parentId);
    await deps.loadChildren(node.parentId, backend);
    deps.expandNode?.(node.parentId);
  }
}

/**
 * Handles submit errors with proper error codes (428, 424)
 * Returns true if submission should be retried with userHasConfirmed=true, false if it should be aborted
 */
export async function handleSubmitError(
  error: unknown,
  node: TreeNode,
  deps: ISubmitDependencies
): Promise<{ shouldRetry: boolean; shouldAbort: boolean }> {
  if (!ApiException.isApiException(error)) {
    deps.showSnackbar('Unexpected error occurred while submitting.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }

  // The backend returns the validation message in the ProblemDetails `detail` field as plain text
  // (multi-line, newline-separated). Read it via getProblemDetail; never treat error.result as a string.
  const detail = getProblemDetail(error);

  if (error.status === 428) {
    // Requires confirmation - show additional dialog
    const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, deps.getData);
    const message = detail ?? `This ${entityTypeLabel} has issues. Are you sure you want to submit anyway?`;
    const isSubstrateWarning = detail?.includes('This is a substrate') ?? false;

    const userConfirmed = await deps.showConfirmDialog({
      title: isSubstrateWarning ? 'Submit Substrate' : `Confirm Submit ${entityTypeLabel}`,
      message,
      confirmText: isSubstrateWarning ? 'Submit Substrate' : 'Submit Anyway',
      isDestructive: false,
    });

    if (userConfirmed) {
      return { shouldRetry: true, shouldAbort: false };
    }
    return { shouldRetry: false, shouldAbort: true };
  } else if (error.status === 424) {
    // Cannot submit - show detailed error message
    const entityTypeLabel = TreeNodeFactory.getNodeTypeLabel(node, deps.getData);
    deps.showSnackbar(
      detail ?? `${entityTypeLabel} ${node.label} cannot be submitted due to validation issues.`,
      'error',
      null, // do not hide automatically, leave to user to close manually
      `${entityTypeLabel} ${node.label} cannot be submitted. Please correct the following issues:`
    );
    return { shouldRetry: false, shouldAbort: true };
  } else {
    deps.showSnackbar(getProblemDetail(error) ?? 'Failed to submit.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }
}

/** Thrown when a sample activity save does not produce new server data (e.g. 428 declined or error already surfaced). */
export class SampleActivitySaveIncompleteError extends Error {
  constructor() {
    super('Sample activity save did not complete');
    this.name = 'SampleActivitySaveIncompleteError';
  }
}

const SAMPLE_ACTIVITY_ENTITY_LABEL = 'Sample Activity';

/**
 * Handles sample-activity PUT errors (428 precondition, 424 validation, etc.) the same way as submit:
 * 428 shows a confirmation dialog with the server message; retry uses userHasConfirmed=true on the API.
 */
export async function handleSampleActivitySaveError(
  error: unknown,
  deps: ISubmitDependencies
): Promise<{ shouldRetry: boolean; shouldAbort: boolean }> {
  if (!ApiException.isApiException(error)) {
    deps.showSnackbar('Unexpected error occurred while saving.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }

  // Read the plain-text validation message from ProblemDetails.detail (never from error.result directly).
  const detail = getProblemDetail(error);

  if (error.status === 428) {
    const message = detail ?? `This ${SAMPLE_ACTIVITY_ENTITY_LABEL} has issues. Are you sure you want to save anyway?`;
    const isSubstrateWarning = detail?.includes('This is a substrate') ?? false;

    const userConfirmed = await deps.showConfirmDialog({
      title: isSubstrateWarning ? 'Save Substrate' : `Confirm Save ${SAMPLE_ACTIVITY_ENTITY_LABEL}`,
      message,
      confirmText: isSubstrateWarning ? 'Save Substrate' : 'Save Anyway',
      isDestructive: false,
    });

    if (userConfirmed) {
      return { shouldRetry: true, shouldAbort: false };
    }
    return { shouldRetry: false, shouldAbort: true };
  } else if (error.status === 424) {
    deps.showSnackbar(
      detail ?? `${SAMPLE_ACTIVITY_ENTITY_LABEL} cannot be saved due to validation issues.`,
      'error',
      null,
      `${SAMPLE_ACTIVITY_ENTITY_LABEL} cannot be saved. Please correct the following issues:`
    );
    return { shouldRetry: false, shouldAbort: true };
  } else {
    deps.showSnackbar(getProblemDetail(error) ?? 'Failed to save sample activity.', 'error');
    return { shouldRetry: false, shouldAbort: true };
  }
}

/**
 * PUT sample activity with optional userHasConfirmed retry after 428, matching submitEntity 428 UX.
 * Returns null when the save was aborted or failed after surfacing an error to the user.
 */
export async function updateSampleActivityWithPreconditionRetry(
  backend: IBackend,
  activityId: number,
  request: UpdateSampleActivityRequest,
  deps: ISubmitDependencies
): Promise<SampleActivityDto | null> {
  try {
    return await backend.sampleActivity.update(activityId, undefined, request);
  } catch (err) {
    const errorResult = await handleSampleActivitySaveError(err, deps);
    if (errorResult.shouldRetry) {
      try {
        return await backend.sampleActivity.update(activityId, true, request);
      } catch (err2) {
        deps.showSnackbar(getProblemDetail(err2) ?? 'Failed to save sample activity.', 'error');
        return null;
      }
    }
    return null;
  }
}
